import classNames from 'classnames';

import React, { useState, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { useLanguage } from 'hooks';
import { makeStyles } from '@material-ui/core/styles';
import InputBase from '@material-ui/core/InputBase';
import { Box, Typography, Radio, Button, Tooltip } from '@material-ui/core';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@material-ui/icons/ThumbDownOutlined';
import { HvButton, HvIconButton } from 'components';
import { images } from 'assets/images';
import { COLOR, ENG, FR, GPT } from 'config/constants';

const useStyles = makeStyles({
  box: {
    marginTop: 10,
    marginBottom: 10,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 10,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  textRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  button: {
    width: 200,
    '@media (max-width: 767px)': {
      width: '100%',
    },
  },
  textContent: {
    border: `1px ${COLOR.GREY} solid`,
    borderRadius: 8,
    minHeight: 100,
    padding: 10,
    position: 'relative',
  },
  icon: {
    '&:hover': {
      fill: COLOR.WHITE,
    },
  },
  copyButton: {
    marginRight: 10,
    width: 100,
    border: `1px ${COLOR.LIGHT_BLUE} solid`,
  },
  editButton: {
    marginRight: 10,
    width: 100,
    border: `1px ${COLOR.LIGHT_BLUE} solid`,
  },
  rewriteButton: {
    width: 100,
    border: `1px ${COLOR.LIGHT_BLUE} solid`,
  },
  languageTab: {
    position: 'absolute',
    right: 10,
    top: 10,
  },
  langButton: {
    '&.MuiButton-root': {
      minWidth: 30,
    },
    '&:hover, &:focus': {
      backgroundColor: '#ebebeb',
    },
    backgroundColor: COLOR.LIGHT_GREY,
  },
  selectedLangButton: {
    backgroundColor: COLOR.LIGHT_BLUE,
    '&:hover, &:focus': {
      backgroundColor: COLOR.LIGHT_BLUE,
    },
    color: COLOR.WHITE,
    '&.MuiButton-root': {
      minWidth: 30,
    },
  },
  langText: {
    fontSize: 11,
  },
  feedbackContent: {
    flexDirection: 'row',
    display: 'flex',
  },
  feedbackIcon: {
    color: COLOR.DARK_GREY,
    fontSize: 'medium',
  },
  whiteFeedbackIcon: {
    color: COLOR.WHITE,
    fontSize: 'medium',
  },
  thumbsupButton: {
    border: `1px ${COLOR.GREY} solid`,
    borderRadius: 4,
    '&:focus': {
      backgroundColor: COLOR.LIGHT_BLUE,
      border: 'none',
    },
    '& .Mui-selected, &:hover': {
      color: COLOR.WHITE,
    },
    '@media (max-width: 400px)': {
      '&.MuiButton-root': {
        minWidth: 32,
      },
    },
  },
  dislikeButton: {
    marginLeft: 10,
    border: `1px ${COLOR.GREY} solid`,
    borderRadius: 4,
    '&:focus': {
      backgroundColor: COLOR.LIGHT_BLUE,
      border: 'none',
    },
    '& .Mui-selected, &:hover': {
      color: COLOR.WHITE,
    },
    '@media (max-width: 400px)': {
      '&.MuiButton-root': {
        minWidth: 32,
      },
    },
  },
  dislikedButton: {
    marginLeft: 10,
    border: `1px ${COLOR.GREY} solid`,
    borderRadius: 4,
    backgroundColor: COLOR.LIGHT_BLUE,
  },
  buttonsRow: {
    display: 'flex',
    marginTop: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  descriptionTitle: {
    fontWeight: 'bold',
  },
  generatedText: {
    marginTop: 30,
    minHeight: 120,
    '&.MuiInputBase-root': {
      alignItems: 'flex-start',
    },
    '&.MuiInputBase-root.Mui-disabled': {
      color: COLOR.BLACK,
    },
  },
  flexEndRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 10,
  },
  twoColumnsRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 10,
    justifyContent: 'space-between',
    '@media (max-width: 767px)': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  warning: {
    color: COLOR.RED,
  },
});
interface Props {
  handleGenerate: (textLength: string, descriptionLanguage: string) => void;
  handleClickFeedback: (feedback: number) => void;
  saveEditedDescription: (editedDescription: string) => void;
  generatedText: any;
  editedText: any;
  reachedUsageLimit: boolean;
}
function TextGenerator({
  handleGenerate,
  handleClickFeedback,
  saveEditedDescription,
  generatedText,
  editedText,
  reachedUsageLimit,
}: Props) {
  const intl = useIntl();
  const styles = useStyles();
  const language = useLanguage();
  const inputRef = useRef(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<number>(0);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editedDescription, setEditedDescription] = useState<string>(
    generatedText?.description?.description,
  );
  const [textLength, setTextLength] = useState<string>(GPT.TEXT_LENGTH.SHORT);
  const [descriptionLanguage, setDescriptionLanguage] = useState<string>(language);

  const fmtReachUsageLimit = intl.formatMessage({
    id: 'deliver.descriptionGeneration.reachUsageLimit',
  });
  const fmtLanguage = intl.formatMessage({ id: 'deliver.descriptionGeneration.language' });
  const fmtTextLength = intl.formatMessage({ id: 'deliver.descriptionGeneration.textLength' });
  const fmtShortText = intl.formatMessage({ id: 'deliver.descriptionGeneration.shortText' });
  const fmtMediumText = intl.formatMessage({ id: 'deliver.descriptionGeneration.mediumText' });
  const fmtLongText = intl.formatMessage({ id: 'deliver.descriptionGeneration.longText' });
  const fmtGenerate = intl.formatMessage({ id: 'deliver.descriptionGeneration.generate' });
  const fmtCopy = intl.formatMessage({ id: 'deliver.descriptionGeneration.copy' });
  const fmtCopied = intl.formatMessage({ id: 'deliver.descriptionGeneration.copied' });
  const fmtEdit = intl.formatMessage({ id: 'deliver.descriptionGeneration.edit' });
  const fmtGoodFeedback = intl.formatMessage({ id: 'deliver.descriptionGeneration.goodFeedback' });
  const fmtBadFeedback = intl.formatMessage({ id: 'deliver.descriptionGeneration.badFeedback' });

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      const { length } = inputRef.current.value;
      inputRef.current.setSelectionRange(length, length);
    }
  }, [editedDescription]);
  const getText = () => {
    if (editMode && editedDescription) {
      return editedDescription;
    }
    const description = generatedText?.description?.description;
    return editedText || description || '';
  };
  useEffect(() => {
    getText();
  }, [generatedText]);
  const onCopy = () => {
    navigator.clipboard
      .writeText(getText())
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      })
      .catch((err) => {
        console.log('error copying to clipboard', err);
      });
  };

  const saveDescription = () => {
    if (editedDescription) {
      saveEditedDescription(editedDescription);
    }
    setEditMode(false);
  };

  const onEdit = () => {
    setEditMode(true);
  };

  return (
    <Box className={styles.box}>
      <Box>
        <Typography className={styles.title}>{fmtTextLength}</Typography>
        <Box className={styles.textRow}>
          <Radio
            checked={textLength === GPT.TEXT_LENGTH.SHORT}
            color='secondary'
            onChange={() => {
              setTextLength(GPT.TEXT_LENGTH.SHORT);
            }}
            name='radio-button-demo'
          />
          <Typography>{fmtShortText}</Typography>
          <Radio
            checked={textLength === GPT.TEXT_LENGTH.MEDIUM}
            color='secondary'
            onChange={() => {
              setTextLength(GPT.TEXT_LENGTH.MEDIUM);
            }}
            name='radio-button-demo'
          />
          <Typography>{fmtMediumText}</Typography>
          <Radio
            checked={textLength === GPT.TEXT_LENGTH.LONG}
            color='secondary'
            onChange={() => {
              setTextLength(GPT.TEXT_LENGTH.LONG);
            }}
            name='radio-button-demo'
          />
          <Typography>{fmtLongText}</Typography>
        </Box>
      </Box>
      <Box className={styles.twoColumnsRow}>
        <Box>
          <Typography className={styles.title}>{fmtLanguage}</Typography>
          <Box className={styles.textRow}>
            <Radio
              checked={descriptionLanguage === ENG}
              color='secondary'
              onChange={() => {
                setDescriptionLanguage(ENG);
              }}
              name='radio-button-demo'
            />
            <Typography>en</Typography>
            <Radio
              checked={descriptionLanguage === FR}
              color='secondary'
              onChange={() => {
                setDescriptionLanguage(FR);
              }}
              name='radio-button-demo'
            />
            <Typography>fr</Typography>
          </Box>
        </Box>
        <HvButton
          className={styles.button}
          disabled={reachedUsageLimit}
          onClick={() => {
            setEditMode(false);
            return handleGenerate(textLength, descriptionLanguage);
          }}
        >
          {fmtGenerate}
        </HvButton>
      </Box>
      {reachedUsageLimit && (
        <Box className={styles.flexEndRow}>
          <Typography className={styles.warning}>{fmtReachUsageLimit}</Typography>
        </Box>
      )}

      <Box className={styles.textContent}>
        {!isEmpty(generatedText) ? (
          <Box>
            {editMode ? (
              <InputBase
                className={styles.generatedText}
                multiline
                inputRef={inputRef}
                fullWidth
                readOnly={!editMode}
                autoFocus
                disabled={!editMode}
                onChange={(e) => {
                  setEditedDescription(e.target.value);
                }}
                onFocus={(e) => {
                  const { length } = e.target.value;
                  e.target.setSelectionRange(length, length);
                }}
                value={editedDescription || generatedText?.description?.description}
                inputProps={{ 'aria-label': 'naked' }}
              />
            ) : (
              <InputBase fullWidth readOnly multiline value={getText()} />
            )}
            <Box className={styles.buttonsRow}>
              {editMode ? (
                <Button
                  className={styles.thumbsupButton}
                  onClick={() => {
                    return saveDescription();
                  }}
                >
                  <CheckRoundedIcon className={styles.feedbackIcon} />
                </Button>
              ) : (
                <Box className={styles.row}>
                  <HvIconButton
                    className={styles.copyButton}
                    icon='copy'
                    text={copied ? fmtCopied : fmtCopy}
                    onClick={onCopy}
                  />
                  <HvIconButton
                    className={styles.editButton}
                    icon='edit'
                    text={fmtEdit}
                    onClick={onEdit}
                  />
                </Box>
              )}

              {editMode ? null : (
                <Box className={styles.feedbackContent}>
                  <Tooltip title={fmtGoodFeedback}>
                    <Button
                      className={styles.thumbsupButton}
                      onClick={() => {
                        setFeedback(1);
                        return handleClickFeedback(1);
                      }}
                    >
                      <ThumbUpOutlinedIcon
                        className={feedback === 1 ? styles.whiteFeedbackIcon : styles.feedbackIcon}
                      />
                    </Button>
                  </Tooltip>
                  <Tooltip title={fmtBadFeedback}>
                    <Button
                      className={styles.dislikeButton}
                      onClick={() => {
                        setFeedback(-1);
                        return handleClickFeedback(0);
                      }}
                    >
                      <ThumbDownOutlinedIcon
                        className={feedback === -1 ? styles.whiteFeedbackIcon : styles.feedbackIcon}
                      />
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default TextGenerator;
